import { RadioGroup } from '@arcadiapower/shrike';
import { Button } from '~/components/button';
import { Form } from '~/components/form';
import { type ImageType, ImageSelector, SELECT_UTILITY_IMAGE } from '~/components/image';
import { Container, TwoColumnGrid } from '~/components/layout';
import { Title, Description, Text } from '~/components/typography';
import { useCopy, useForm } from '~/hooks';
import { BillingDataSource, Utility } from '~/typings/context';

export const BillingSelection = ({ context, next, overrideCopy }: BillingSelectionProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const [form, updateForm] = useForm({
    selection: context.selection,
  });
  const isValid = !!form.selection;
  const handleSelect = (selection: BillingDataSource) =>
    updateForm(selection, { name: 'selection', isValid: true });
  return (
    <TwoColumnGrid testId="screen-BillingSelection">
      <TwoColumnGrid.Primary>
        <Title>{copy.title}</Title>
        <Description mb={5}>{copy.description}</Description>
        <Form isValid={isValid} onSubmit={() => next(form as BillingSelectionForm)}>
          <RadioGroup
            name="billing-experience-selection"
            options={context.options.map(experience => ({
              id: experience,
              children: (
                <Container key={experience} onClick={() => handleSelect(experience)}>
                  <Text>{copy[experience].title}</Text>
                  <Text my={2}>{copy[experience].description}</Text>
                </Container>
              ),
              value: experience,
            }))}
            isChecked={(_, value) => value === form.selection}
            onChange={handleSelect}
            width="initial"
          />
          <Button mode="primary" mt={3} type="submit" disabled={!isValid}>
            {copy.submit}
          </Button>
        </Form>
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.imageType} />
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const defaultCopy = (context: BillingSelectionContext) => {
  const utility = context.utility.displayName;
  return {
    title: 'Now, choose your billing preference.',
    description:
      "No matter which option you select, you're guaranteed to save on your monthly energy costs.",
    imageType: SELECT_UTILITY_IMAGE as ImageType,
    acb: {
      description: `Automatically pay Arcadia each month for your ${utility} bill and your contribution to the solar farm. You’ll receive a clean energy statement each month and see your solar savings stack up over time.`,
      title: 'Pay it all through Arcadia',
    },
    ucb: {
      description: `Keep paying your ${utility} bill like you do now. They’ll keep part of your payment, send the rest to the solar farm, and apply the solar credits you earn. We won’t send you a clean energy statement.`,
      title: `Pay through ${utility}`,
    },
    dual: {
      description: `Keep paying your ${utility} bill like you do now, but note that it will be lower since it won’t include your payment to the solar farm — you’ll pay that through Arcadia. We’ll send you a clean energy statement each month for your solar farm contribution.`,
      title: 'Pay separately',
    },
    submit: 'Continue',
  };
};

export type BillingSelectionCopy = ReturnType<typeof defaultCopy>;

export interface BillingSelectionProps {
  context: BillingSelectionContext;
  next: (payload: BillingSelectionForm) => void;
  overrideCopy?: (context: BillingSelectionContext) => Partial<BillingSelectionCopy>;
}

export type BillingSelectionForm = {
  selection: BillingDataSource;
};

export interface BillingSelectionContext {
  options: BillingDataSource[];
  selection?: BillingDataSource;
  utility: Utility;
}
